import Vue from 'vue';
import store from '@/store';
import resetState from '@/helpers/resetState';
import { paramsSerializer } from '@/helpers/serializers';
import { ABI } from './ABI';
import CONFIG from '@/config';
import axios from 'axios/index';
import { formatData } from '@/constants/commonContent';
import { messages } from '@/constants/messages';
import { getTransactionPromise } from '../../../helpers/transaction';
import { Contract } from 'web3-eth-contract';

const FEATURE_NAME = 'MarketFeature';

let from;

async function contract(): Promise<Contract> {
  from = {from: store.getters.getWallet};
  const address = store.getters.getFeatureAddress(FEATURE_NAME);
  return new Promise(resolve => {
    let contract = new window.web3.eth.Contract(ABI, address);
    resolve(contract);
  });
}

const initialState = {
  orders: {},
  trades: {},
  lastTrade: {},
  orderCounter: null,
  activeOrderCounter: null,
  tradeCounter: null,
  ethToUSDPrice: null,
  chartData: [],
  dailyHigh: {},
  dailyVolume: {},
  marketCup: {}
};

const state = {
  orders: initialState.orders,
  trades: initialState.trades,
  lastTrade: initialState.lastTrade,
  orderCounter: initialState.orderCounter,
  activeOrderCounter: initialState.activeOrderCounter,
  tradeCounter: initialState.tradeCounter,
  ethToUSDPrice: initialState.ethToUSDPrice,
  chartData: initialState.chartData,
  dailyHigh: initialState.dailyHigh,
  dailyVolume: initialState.dailyVolume,
  marketCup: initialState.marketCup
};

const getters = {
  getOrders: state => state.orders,
  getActiveOrders: state => Object.values(state.orders).filter((order: any) => order.isActive),
  getTrades: state => state.trades,
  getLastTrade: state => state.lastTrade,
  getActiveTradesList: state => Object.values(state.trades).filter((trade: any) => trade.isActive),
  getEthToUSDPrice: state => state.ethToUSDPrice,
  getActiveOrderCounter: state => state.activeOrderCounter,
  getChartData: state => state.chartData,
  getDailyHigh: state => state.dailyHigh,
  getDailyVolume: state => state.dailyVolume,
  getMarketCup: state => state.marketCup
};

const mutations = {
  setOrder: (state, item) => {
    Vue.set(state.orders, item.id, item);
  },
  setTrade: (state, item) => {
    Vue.set(state.trades, item.id, item);
  },
  setLastTrade: (state, item) => {
    state.lastTrade = item;
  },
  setOrderCounter: (state, item) => {
    state.orderCounter = item;
  },
  setTradeCounter: (state, item) => {
    state.tradeCounter = item;
  },
  setActiveOrderCounter: (state, item) => {
    state.activeOrderCounter += item;
  },
  resetActiveOrderCounter: state => {
    state.activeOrderCounter = 0;
  },
  setEthToUSDPrice: (state, item) => {
    state.ethToUSDPrice = item;
  },
  setChartData: (state, item) => {
    state.chartData = item;
  },
  setDailyHigh: (state, item) => {
    state.dailyHigh = item;
  },
  setDailyVolume: (state, item) => {
    state.dailyVolume = item;
  },
  setMarketCup: (state, item) => {
    state.marketCup = item;
  },
  resetMarketFeature: state => {
    resetState(state, initialState);
  }
};

const actions = {
  initMarketFeature({ dispatch, commit, getters }) {
    let sent;
    dispatch('getOrdersDapp');
    dispatch('getTradesDapp');
    dispatch('getEthereumLastPrice');
    setInterval(() => {
      dispatch('getEthereumLastPrice');
    }, 3600000);

    //Events
    contract().then(contract => {
      let transactionHash = '0x';
      contract.events.MarketOrderEvent().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          const orderId = res.returnValues.orderId;
          transactionHash = res.transactionHash;
          commit('resetActiveOrderCounter');
          dispatch('getOrderDapp', orderId);
          commit('newOrderPending', false);
          commit('newCancelOrderPending', null);
          dispatch('getFreezedOfDapp', getters.getWallet);
        }
      });
      contract.events.MarketTradeEvent().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          const tradeId = res.returnValues.tradeId;
          transactionHash = res.transactionHash;
          commit('resetActiveOrderCounter');
          dispatch('getTradeDapp', tradeId).then(trade => {
            dispatch('getOrderDapp', trade.orderId);
          });
          commit('newTradePending', null);
          dispatch('getFreezedOfDapp', getters.getWallet);
          commit('resetMailPending', 'tradeNow');
        }
      });
    });
  },
  /**
   * Cancel order for owner
   * @param id {number}
   */
  cancelMarketOrderDapp({ commit }, id) {
    commit('newCancelOrderPending', id);
    contract().then(contract => {
      contract.methods.cancelMarketOrder(id)
        .send(from, (err) => {
          if (err) {
            commit('newCancelOrderPending', null);
            Vue.toasted.error(messages.notCanceledOrder);
          }
        });
    });
  },
  /**
   * Add order
   * @param amount {number}
   * @param price {number}
   */
  addMarketOrderDapp({ commit, dispatch }, data) {
    contract().then(contract => {
      dispatch('countDealDapp', { amount: data.amount, price: data.price }).then(res => {
        contract.methods.addMarketOrder(
            data.mode,
            data.amount,
            data.price,
          )
          .send({...from, value: data.mode === 1 ? res : 0 }, (err) => {
            if (err) {
              commit('newOrderPending', false);
              Vue.toasted.error(messages.canceledOrder);
            }
          }
        );
      });
    });
  },
  /**
   * Add trade for order
   * @param orderId {number}
   * @param amount {number}
   * @param {value} {number}
   * @return {Promise}
   */
  addMarketTradeDapp({ commit, dispatch, getters }, data) {
    commit('newTradePending', data.orderId);
    contract().then(contract => {
      contract.methods.addMarketTrade(data.orderId, data.amount)
        .send({...from, value: data.fee }, (err, res) => {
          if (err) {
            commit('newTradePending', null);
            Vue.toasted.error(messages.canceledTrade);
          } else {
            const order = getters.getOrders[data.orderId];
            const from = order.mode === 1 ? getters.getWallet : order.address;
            const to = order.mode === 1 ? order.address : getters.getWallet;
            store.dispatch('sendTradeMailExt', {
              from,
              to,
              amount: `${data.amount} ${getters.getTokenSymbol}`,
              price: `${order.price} ETH`,
              time: Vue.moment().format(formatData.dateTime),
              mode: order.mode,
              hasEscrow: getters.isUsedEscrow,
              tx: res
            });
          }
        });
    });
  },
  /**
   * Get order
   * @param id {number}
   * @res[0] "address creator" {string}
   * @res[1] "amount" {number}
   * @res[2] "remainder" {number}
   * @res[3] "price" {string}
   * @res[4] "mode" {number}
   * @res[5] "start time" {number}
   * @res[6] "send time" {number}
   * @res[7] "is active order" {Boolean}
   */
  getOrderDapp({ commit }, id) {
    contract().then(contract => {
      contract.methods.getMarketOrder(id)
        .call()
        .then(res => {
          let data = {
            id,
            address: res[0],
            amount: parseInt(res[1]),
            remainder: parseInt(res[2]),
            price: res[3],
            mode: parseInt(res[4]),
            startTime: parseInt(res[5]),
            endTime: parseInt(res[6]),
            isActive: res[7]
          };
          if (data.isActive) {
            commit('setActiveOrderCounter', 1);
          }
          commit('setOrder', data);
        })
        .catch(console.error);
    });
  },
  /**
   * Get count of active trade for order
   * @param id "orderId" {number}
   * @return {Promise<number>}
   * @res[0] "trade count" {number}
   */
  getTradesOfOrderDapp({ commit }, id) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getTradesOfOrderCounter(id)
          .call()
          .then(res => {
          resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  getOrdersDapp({ dispatch }) {
    dispatch('getOrderCounterDapp').then(result => {
      for (let i = 0; i < result; i++) {
        dispatch('getOrderDapp', i);
      }
    });
  },
  /**
   * Get count of order
   * @return {Promise<number>}
   * @res[0] "order count" {number}
   */
  getOrderCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getMarketOrderCounter()
          .call()
          .then(res => {
            commit('setOrderCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get count of trade
   * @return {Promise<number>}
   * @res[0] "trade count" {number}
   */
  getMarketTradeCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getMarketTradeCounter()
          .call()
          .then(res => {
            commit('setTradeCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  getTradesDapp({ dispatch, commit }) {
    dispatch('getMarketTradeCounterDapp').then(result => {
      for (let i = 0; i < result; i++) {
        dispatch('getTradeDapp', i).then(trade => {
          if (result - 1 === i) commit('setLastTrade', trade);
        });
      }
    });
  },
  /**
   * Get trade
   * @param id {number}
   * @return {Promise<Object>}
   * @res[0] "address creator" {string}
   * @res[1] "orderId" {number}
   * @res[2] "amount" {number}
   * @res[3] "start time" {number}
   * @res[4] "status" {number}
   */
  getTradeDapp({ commit }, id) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getMarketTrade(id)
          .call()
          .then(res => {
            let data = {
              id,
              creator: res[0],
              orderId: parseInt(res[1]),
              amount: parseInt(res[2]),
              startTime: parseInt(res[3]),
              status: parseInt(res[4])
            };
            commit('setTrade', data);
            resolve(data);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get count token payment
   * @param orderId {number}
   * @param amount {number}
   * @res[0] "count" {number}
   */
  countTokenPaymentByOrderIdDapp({ dispatch, commit }, data) {
    contract().then(contract => {
      contract.methods.countTokenPaymentByOrderId(data.orderId, data.amount)
        .call()
        .then(res => {
          data.fee = parseInt(res[0]);
          dispatch('addMarketTradeDapp', data);
        })
        .catch(() => { Vue.toasted.error(messages.canceledTrade) });
    });
  },
  /**
   * Get count order payment
   * @param orderId {number}
   * @res[0] "count" {number}
   */
  countOrderPaymentsDapp({ dispatch }, data) {
    contract().then(contract => {
      contract.methods.countOrderPayments(data.orderId)
        .call()
        .then(res => {
          data.fee = parseInt(res[0]);
          dispatch('confirmOrderTradesDapp', data);
        })
        .catch(() => {
          Vue.toasted.error(messages.notAcceptedTrade);
        });
    });
  },
  /**
   * Confirm trade for order
   * @param orderId {number}
   * @param { value } {number}
   */
  confirmOrderTradesDapp({ commit }, data) {
    commit('newTradePending', data.orderId);
    contract().then(contract => {
      contract.methods.confirmOrderTrades(data.orderId)
        .send({...from, value: data.fee }, (err) => {
          if (err) {
            commit('newTradePending', null);
            Vue.toasted.error(messages.notAcceptedTrade);
          }
        });
    });
  },
  /**
   * Get last price  in usd per one ethereum
   * @param  {Object}
   * @param  params.fsym {string}
   * @param  params.tsyms {string}
   * @data.usd {number}
   */
  getEthereumLastPrice({ commit }) {
    axios
      .get(CONFIG.cryptocompare!.url, {
        params: CONFIG.cryptocompare!.params,
        paramsSerializer
      })
      .then(res => {
        if (res.data['USD']) {
          commit('setEthToUSDPrice', parseInt(res.data['USD']));
        }
      });
  },
  clearChartData({ commit }) {
    commit('setChartData', []);
  },
  getChartDataExt({ getters, commit }, fromTo) {
    getters.chartData.getChartData(fromTo).then(res => {
      commit('setChartData', res);
    });
  },
  getDailyHighExt({ getters, commit }) {
    getters.chartData.getDailyHigh().then(res => {
      commit('setDailyHigh', res);
    });
  },
  getDailyVolumeExt({ getters, commit }) {
    getters.chartData.getDailyVolume().then(res => {
      commit('setDailyVolume', res);
    });
  },
  getMarketCupExt({ getters, commit }) {
    getters.chartData.getMarketCup().then(res => {
      commit('setMarketCup', res);
    });
  }
};

export default {
  state,
  getters,
  mutations,
  actions
};
